import { Link } from 'react-router-dom';
import { Heart, ShoppingBag } from 'lucide-react';
import { useStore } from '@/store';
import { useStoreInfo } from '@/lib/store';
import { motion } from 'framer-motion';
import { SocialMediaLinks } from '@/components/ui/social-media-links';

interface TopNavbarProps {
  onMobileMenuOpen: () => void;
}

export function TopNavbar({ onMobileMenuOpen }: TopNavbarProps) {
  const cart = useStore((state) => state.cart);
  const wishlist = useStore((state) => state.wishlist);
  const { storeInfo } = useStoreInfo();

  const totalCartItems = cart.reduce((sum, item) => {
    if (item.selectedVariations && item.selectedVariations.length > 0) {
      return sum + item.selectedVariations.reduce((varSum, v) => varSum + v.quantity, 0);
    }
    return sum + item.quantity;
  }, 0);

  const logoSrc = storeInfo?.site_logo ? `http://leeyahtrend.com.ng/${storeInfo.site_logo}` : '/logo.png';

  const socialLinks = storeInfo?.social || {
    facebook: storeInfo?.shop_fb || '',
    twitter: storeInfo?.shop_x || '',
    instagram: storeInfo?.shop_ig || '',
    tiktok: storeInfo?.tiktok_url || '',
    whatsapp: storeInfo?.shop_wa || '',
    telegram: storeInfo?.shop_tg || ''
  };

  return (
    <>
      {/* Desktop Top Navigation */}
      <nav className="hidden md:block sticky top-0 z-50 bg-white border-b-2 border-brand-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          {/* Top Bar */}
          <div className="flex h-20 items-center justify-between">
            {/* Logo */}
            <Link to="/home" className="flex items-center flex-shrink-0">
              <img
                src={logoSrc}
                alt="Reniss Collections"
                className="h-14 w-auto object-contain"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.onerror = null;
                  target.src = '/logo.png';
                }}
              />
            </Link>

            {/* Center Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <Link 
                to="/home" 
                className="text-gray-700 hover:text-brand-600 font-semibold transition-colors"
              >
                Home
              </Link>
              <Link 
                to="/products" 
                className="text-gray-700 hover:text-brand-600 font-semibold transition-colors"
              >
                All Products
              </Link>
              <Link 
                to="/categories" 
                className="text-gray-700 hover:text-brand-600 font-semibold transition-colors"
              >
                Categories
              </Link>
              <Link 
                to="/track" 
                className="text-gray-700 hover:text-brand-600 font-semibold transition-colors"
              >
                Track Order
              </Link>
              <Link 
                to="/about" 
                className="text-gray-700 hover:text-brand-600 font-semibold transition-colors"
              >
                About
              </Link>
              <Link 
                to="/contact" 
                className="text-gray-700 hover:text-brand-600 font-semibold transition-colors"
              >
                Contact
              </Link>
              <Link 
                to="/faq" 
                className="text-gray-700 hover:text-brand-600 font-semibold transition-colors"
              >
                FAQ
              </Link>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              <div className="hidden lg:flex items-center">
                <SocialMediaLinks socialLinks={socialLinks} size="sm" theme="light" />
              </div>
              <Link to="/wishlist" className="relative p-2 rounded-full hover:bg-brand-50 transition-all">                <Heart className="h-5 w-5 text-gray-700" />
                {wishlist.length > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-brand-500 text-[10px] font-bold text-white"
                  >
                    {wishlist.length}
                  </motion.span>
                )}
              </Link>

              <Link to="/cart" className="relative p-2 rounded-full hover:bg-brand-50 transition-all">
                <ShoppingBag className="h-5 w-5 text-gray-700" />
                {totalCartItems > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-brand-500 text-[10px] font-bold text-white"
                  >
                    {totalCartItems}
                  </motion.span>
                )}
              </Link>
            </div>
          </div>

        </div>
      </nav>

      {/* Mobile Header */}
      <div className="md:hidden sticky top-0 z-50 bg-white border-b-2 border-brand-100 shadow-sm">
        <div className="flex h-16 items-center justify-between px-4">
          {/* Menu Button */}
          <button
            onClick={onMobileMenuOpen}
            className="px-3 py-2 rounded-lg hover:bg-brand-50 transition-colors"
          >
            <span className="text-sm font-bold tracking-wide text-brand-600">
              EXPLORE
            </span>
          </button>

          {/* Logo */}
          <Link to="/home" className="flex items-center flex-1 justify-center">
            <img
              src={logoSrc}
              alt="Reniss Collections"
              className="h-10 w-auto object-contain"
              onError={(e) => {
                const target = e.currentTarget;
                target.onerror = null;
                target.src = '/logo.png';
              }}
            />
          </Link>

          {/* Cart Button */}
          <Link to="/cart" className="relative p-2 rounded-lg hover:bg-brand-50 transition-colors">
            <ShoppingBag className="h-5 w-5 text-gray-700" />
            {totalCartItems > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-brand-500 text-[10px] font-bold text-white"
              >
                {totalCartItems}
              </motion.span>
            )}
          </Link>
        </div>
        <div className="flex items-center justify-center py-1 border-t border-brand-100">
          <SocialMediaLinks socialLinks={socialLinks} size="sm" theme="light" />
        </div>
      </div>
    </>
  );
}
